import {
  integrationGPONPayloadSchema,
  IntegrationGPONPayloadSchema,
  useDeviceCodes,
  useIntegrationGPONHPMCreate,
  useIntegrationGPONHPMs,
} from "@/api/generate-hpdb";
import { useRFS } from "@/api/rfs";
import { FieldSelect } from "@/components/form/field-select";
import { FormContainer } from "@/components/form/form-container";
import { SubmitButton } from "@/components/ui/button";
import { MISSING_ID_IN_ROUTE } from "@/utils/constant";
import { swalError, swalSuccess } from "@/utils/lib/swal";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

export function CreateDataGPONForm({
  isReadOnly = false,
  onFormDataChange,
}: {
  isReadOnly: boolean;
  onFormDataChange: (hasData: boolean) => void;
}) {
  const { id } = useParams();
  if (!id) {
    throw new Error(MISSING_ID_IN_ROUTE);
  }

  const { data: rfs } = useRFS({ id });
  const { data: integrationGPONHPMs } = useIntegrationGPONHPMs({ rfsId: id });
  const { data: deviceCodes } = useDeviceCodes(
    {
      deviceName: rfs?.oltId,
    },
    {
      enabled: Boolean(rfs?.oltId),
    },
  );
  const integrationGPONHPM = integrationGPONHPMs?.[0];
  const deviceCodeoptions =
    deviceCodes?.data?.map((deviceCode) => ({
      value: deviceCode.deviceName,
      label: deviceCode.deviceName,
    })) || [];

  useEffect(() => {
    const hasData = !!integrationGPONHPM;
    onFormDataChange(hasData);
  }, [integrationGPONHPM, onFormDataChange]);

  const form = useForm<IntegrationGPONPayloadSchema>({
    resolver: zodResolver(integrationGPONPayloadSchema),
    defaultValues: {
      deviceCode: integrationGPONHPM?.deviceCode,
      rfs: {
        id: id,
      },
    },
    values: {
      deviceCode: integrationGPONHPM?.deviceCode ?? "",
      rfs: {
        id: id,
      },
    },
  });

  const { mutate, isPending } = useIntegrationGPONHPMCreate();

  const handleSubmit = (values: IntegrationGPONPayloadSchema) => {
    mutate(
      {
        data: {
          deviceCode: values.deviceCode,
          rfs: {
            id: id,
          },
        },
      },
      {
        onSuccess: () => {
          swalSuccess.fire({
            text: "Success create data GPON",
          });
        },
        onError: (error) => {
          swalError.fire({
            text: error.message,
          });
        },
      },
    );
  };

  return (
    <FormContainer form={form} onSubmit={handleSubmit}>
      <FieldSelect
        name="deviceCode"
        label="Device OLT"
        options={deviceCodeoptions}
        isDisabled={isReadOnly || Boolean(integrationGPONHPM)}
        hasRequiredAsterisk
      />
      <SubmitButton
        loading={isPending}
        disabled={isReadOnly || Boolean(integrationGPONHPM)}
        className="ml-auto"
      />
    </FormContainer>
  );
}
